<template>
  <div :class="['input-wrapper', { input_error: error }, { input_disabled: disabled }]">
    <input
      v-if="isTel"
      :id="id"
      v-mask="inputMask"
      :tabindex="disabled ? -1 : 1"
      v-bind="$attrs"
      :value="currentValue"
      :class="['input__field', { textarea: isTextarea }]"
      :name="name"
      placeholder=""
      @input="onInput($event.target.value)"
      @focus="handleFocus"
      @paste="onPaste"
    >
    <input
      v-else
      :id="id"
      :tabindex="disabled ? -1 : 1"
      v-bind="$attrs"
      :value="currentValue"
      :class="['input__field', { textarea: isTextarea }]"
      :name="name"
      :maxlength="max"
      placeholder=""
      :style="validatePatternVin ? 'text-transform: uppercase;' : ''"
      @input="onInput($event.target.value)"
      @keydown="onKeyDown"
      @focus="handleFocus"
      @blur="handleBlur"
      @paste="onPaste"
    >
    <template v-if="labelHtml">
      <label
        v-if="label"
        :for="id"
        class="input__label"
        v-html="label"
      />
      <span v-if="required" class="input__label-required"> *</span>
    </template>
    <template v-else>
      <label
        v-if="label"
        :for="id"
        class="input__label autofill"
        :class="{ 'input__label-top': currentValue.length > 0 }"
      >
        {{ label }}
        <span v-if="required" class="input__label-required">*</span>
      </label>
    </template>
    <span v-if="error && (errorMsg || errorMsgLocal)" class="input__error-msg">
      {{ errorMsg || errorMsgLocal }}
    </span>
    <ErrorIcon
      v-if="error && errorMsg"
      class="input__error-msg-icon"
      :class="{ 'input__error-msg-icon-top': isTextarea }"
    />
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { mask, tokens } from 'vue-the-mask'
  import ErrorIcon from '@/assets/img/icons/error.svg?inline'
  import { isMd } from '@/config'

  export default {
    name: 'BaseInput',
    components: { ErrorIcon },
    directives: { mask },
    inheritAttrs: false,
    props: {
      name: { type: String, default: '' },
      onlyUppercase: { type: Boolean, default: false },
      value: { type: String, default: '' },
      label: { type: String, default: '' },
      max: { type: String, default: '9999' },
      customMessageText: { type: String, default: '' },
      errorMsg: { type: String, default: '' },
      errorCustom: { type: String, default: '' },
      disabled: { type: Boolean, default: false },
      labelHtml: { type: Boolean, default: false },
      defaultInput: { type: String, default: '' },
      validatePatternState: { type: Boolean, default: false },
      validatePatternVin: { type: Boolean, default: false }
    },
    data () {
      return {
        error: null,
        currentValue: this.value,
        errorMsgLocal: '',
        min: 8
      }
    },
    computed: {
    ...mapGetters(['getPhoneType']),
    id () {
      return `input-${this._uid}`
    },
    required () {
      return 'required' in this.$attrs && this.$attrs.required
    },
    inputMask () {
      if (this.getPhoneType === '+373') {
        return this.$attrs.type === 'tel' ? `+###########` : ''
      } else {
        return this.$attrs.type === 'tel' ? `+380-##-###-##-##` : ''
      }
    },
    isTextarea () {
      return this.$attrs.type === 'textarea'
    },
    isTel () {
      return this.$attrs.type === 'tel'
    }
    },
    watch: {
      value (newValue) {
        if (newValue !== this.currentValue) {
          this.currentValue = newValue
          this.$nextTick(() => {
            if (this.$refs.input) {
              this.$refs.input.value = newValue
            }
          })
        }
        if (newValue === '') {
          this.error = false
          this.errorMsgLocal = ''
        }
      },
      errorMsg: {
        handler (msg) {
          this.error = !!msg
        },
        immediate: true
      }
    },
    mounted () {
      tokens.X = { pattern: /[+\d]/, transform: v => v }
    },
    methods: {
      handleFocus (e) {
        if (this.validatePatternState && e.target.value.length > 0) {
          return
        }
        this.error = false
      },
      handleBlur (e) {
        if (this.validatePatternState && e.target.value.length === 0) {
          this.error = false
        }
      },
      onPaste (event) {
        if (!this.validatePatternState && !this.validatePatternVin) return

        event.preventDefault()

        const pastedData = (event.clipboardData || window.clipboardData).getData('text')
        if (!pastedData || pastedData.trim() === '') return
        const input = event.target
        const start = input.selectionStart
        const end = input.selectionEnd

        let allowedRegex

        if (this.validatePatternVin) {
          allowedRegex = /^[A-Za-z0-9]$/i
        }

        if (this.validatePatternState) {
          const latin = /^[A-Za-z0-9\s]$/
          const cyrillic = /^[А-Яа-яЁёЇїІіЄєҐґ0-9\s]$/

          allowedRegex = char => latin.test(char) || cyrillic.test(char)
        }

        let result = ''

        for (const char of pastedData) {
          const isValid = typeof allowedRegex === 'function'
            ? allowedRegex(char)
            : allowedRegex.test(char)

          if (!isValid) break
          result += char
        }

        const newValue =
          input.value.slice(0, start) +
          result +
          input.value.slice(end)

        const finalValue = newValue.slice(0, this.max)

        if (!finalValue) return
        this.onInput(finalValue)

        this.$nextTick(() => {
          const cursor = start + result.length
          input.setSelectionRange(cursor, cursor)
        })
      },
      onKeyDown (event) {
        if (this.validatePatternState) {
          if (event.key === ' ' || event.code === 'Space') {
            event.preventDefault()
          }
        }
        if (this.validatePatternVin) {
          if (
            event.ctrlKey ||
            event.metaKey ||
            event.altKey
          ) {
            return
          }

          const allowedKeys = [
            'Backspace', 'Delete',
            'ArrowLeft', 'ArrowRight',
            'Tab', 'Enter',
            'Home', 'End'
          ]

          if (allowedKeys.includes(event.key)) return

          if (!/^[A-Za-z0-9]$/.test(event.key)) {
            event.preventDefault()
          }
        }
      },

      onInput (value) {
        if (this.onlyUppercase) {
          value = value.toUpperCase()
        }

        value = value.replace(/\s+/g, '')

        if (this.validatePatternVin) {
          value = value.replace(/[^A-Za-z0-9]/g, '')
        }

        if (this.validatePatternState) {
          const max = isMd ? 6 : 8
          const latinRegex = /^[A-Z0-9]*$/
          const cyrillicRegex = /^[А-ЯЁЇІЄҐ0-9]*$/

          if (
            value.length !== max ||
            (!latinRegex.test(value) && !cyrillicRegex.test(value))
          ) {
            this.error = true
            const message = this.customMessageText
            this.errorMsgLocal = message
            this.$emit('update:errorCustom', message)
          } else {
            this.error = false
            this.errorMsgLocal = ''
            this.$emit('update:errorCustom', '')
          }
        }

        this.currentValue = value
        this.$emit('input', value)
      }
    }
  }
</script>

<style lang="scss">
$padding-for-error: 20px;

//.service-request-page__form-input.input-wrapper input:not(:placeholder-shown) ~ label,
.service-request-page__form-input.input-wrapper input:-webkit-autofill ~ label /* <<<< Add these */
{
  display: none;
}

.service-request-page__form-input.input-wrapper input:-webkit-autofill ~ label.autofill {
  display: block;
  top: 6px;
  font-size: 10px;
  line-height: 20px;
  color: $c-gray;
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  box-shadow: 0 0 0 30px #fff inset !important;
  transition: #fff 5000s ease-in-out 0s;
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-transition: "color 9999s ease-out, background-color 9999s ease-out";
  -webkit-transition-delay: 9999s;
}

.input {
  &-wrapper {
    &:not(:placeholder-shown, :active, :focus-visible) {
      label: {
        opacity: 0;
      }
    }

    position: relative;
    min-width: 290px;

    & input:focus + .input__label,
    & textarea:focus + .input__label {
      top: 6px;
      font-size: 10px;
      line-height: 20px;
      color: $c-gray;
    }
  }

  &_disabled {
    pointer-events: none;
  }

  &__label {
    position: absolute;
    top: 19px;
    left: 20px;
    z-index: 1;
    display: inline-block;
    padding-bottom: 1em;
    font-family: NobelWGL-Book;
    font-weight: 400;
    letter-spacing: 1px;
    font-size: 16px;
    color: $c-gray-new;
    user-select: none;
    transition: all 0.2s;

    &-top {
      top: 6px;
      font-size: 10px;
      line-height: 20px;
      color: $c-gray;
    }

    &-required {
      color: $c-active-light;
    }
  }

  &__field {
    display: block;
    width: 100%;
    height: 56px;
    padding: 0 20px;
    padding-top: 18px;
    font-family: NobelWGL-Book;
    font-weight: 400;
    font-size: 18px;
    line-height: 30px;
    letter-spacing: 1px;
    border: 1px solid $c-gray-light;
    outline: none;
    caret-color: $c-black;
    appearance: none;
    transition: 0.3s;
    border-radius: 0;

    &:invalid {
      box-shadow: none;
    }

    &.textarea {
      min-height: 168px;
      padding-top: 20px;
      padding-bottom: 20px;
      overflow: auto;
      line-height: 1.4;
      resize: none;

      @include custom-scrollbar();
    }

    &-container {
      position: relative;

      &::after {
        // error icon
        position: absolute;
        top: 50%;
        right: 5%;
        display: inline-block;
        width: 1.5em;
        height: 1.5em;
        padding-top: 0.25em;
        color: $c-white;
        text-align: center;
        content: "!";
        background-color: $c-active;
        border-radius: 50%;
        opacity: 0;
        transition: opacity 0.3s;
        transform: translateY(-50%);
      }
    }

    @include hover {
      background-color: $gray;
    }

    &:focus {
      background-color: $c-white;
      border-color: $c-gray-new;
      box-shadow: 0 2px 10px 0 $c-shadow;
    }
  }

  &__error-msg {
    position: absolute;
    bottom: 14px;
    left: 0;
    height: 0;
    padding-right: 20px;
    font-size: 12px;
    color: $c-error;

    &-icon {
      position: absolute;
      top: 59px;
      right: 0;

      path {
        fill: $c-error;
      }

      &-top {
        top: 171px;
      }
    }
  }

  &_error {
    padding-bottom: $padding-for-error;

    .input {
      &__field {
        padding-right: 3.5em;
        border-color: $c-error;

        &-container {
          &::after {
            opacity: 1;
          }
        }
      }
    }
  }
}
</style>
